import './PricingTablePage.scss';

import * as React from 'react';
import { useRef, useState } from 'react';
import * as ReactDom from 'react-dom';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { translate } from '~l10n/client';
import { atom } from 'jotai';

import { ViewPricingTableEventAdapter } from '~analytics/adapters/ViewPricingTableEventAdapter';
import { Badge } from '~shared/components/Badge';
import { IconChevron } from '~shared/components/icons/standard';
import { Link } from '~shared/components/Link';
import { Spinner } from '~shared/components/Spinner';
import { Tab, TabGroup } from '~shared/components/TabsV2';
import { toast } from '~shared/components/Toast';
import {
  useSelectInitialTeamId,
  useSelectedTeamQuery,
} from '~teacher/components/Account/hooks';
import {
  PlanColumn,
  ViewTeamsAlertMessage,
  ViewingPricingPlanWithoutPermission,
} from '~teacher/components/PricingTable';
import { BillingPeriod } from '~teacher/components/PricingTable/BillingPeriod';
import {
  useCurrentPlan,
  useShowShowbieCompleteOffer,
} from '~teacher/components/PricingTable/hooks';
import { LegacyPlanBanner } from '~teacher/components/PricingTable/LegacyPlanBanner';
import { usePricingPlans } from '~teacher/hook/usePlanPrices';
import { queryClient } from '~teacher/queryClient';

import platform from '../../../../../shared/Platform';

export const seatCountAtom = atom<number>(2);

function PricingTablePage() {
  useSelectInitialTeamId();

  const currentSelectedTeam = useSelectedTeamQuery();
  const pricingQuery = usePricingPlans();
  const showbieCompleteOfferQuery = useShowShowbieCompleteOffer();
  const isApp = platform.isWrapperApp();
  const currentPlanQuery = useCurrentPlan();

  const [billingPeriod, setBillingPeriod] = useState(BillingPeriod.Yearly);

  const isMounted = useRef(false);

  if (
    !pricingQuery.data ||
    showbieCompleteOfferQuery.data === undefined ||
    currentSelectedTeam.data === undefined ||
    currentPlanQuery === undefined
  ) {
    return (
      <div data-testid="pricing-table-page" className="my-12 mx-auto">
        <Spinner size={'small'} />
      </div>
    );
  }

  if (pricingQuery.error || showbieCompleteOfferQuery.error) {
    const errorMessage = translate(
      'An unknown error occurred. Please try again later.'
    );
    toast.critical(errorMessage);
    return <>errorMessage</>;
  }

  const offerShowbieCompletePrice = showbieCompleteOfferQuery.data;

  if (isMounted.current === false) {
    new ViewPricingTableEventAdapter({
      source: 'pricing_page',
      experiment_showbie_complete: offerShowbieCompletePrice
        ? 'price'
        : 'no_price',
    }).sendAnalytics();
    isMounted.current = true;
  }

  return (
    <div
      data-testid="pricing-table-page"
      className="pricing-container mx-auto py-13"
    >
      <div className="p-0">
        <div className="">
          <Link
            href={undefined}
            onClick={() => window.history.back()}
            className="text-sm font-sans leading-8 font-600 text-cyan-700 hocus:text-cyan-600 group relative no-underline"
            tabIndex={0}
            key={'back'}
          >
            <div className="flex items-center -ml-2">
              <IconChevron direction="left" />
              {translate('Back')}
            </div>
          </Link>
        </div>

        <div className="flex flex-col gap-2 text-blue-900">
          <div className="flex flex-col gap-2">
            <div className="font-display text-lg font-700 text-blue-900 leading-14 text-center">
              {translate('Choose a Plan')}
            </div>
            {isApp ? (
              <p className="text-base font-sans text-blue-900">
                {translate(
                  'You can’t upgrade Socrative in the app. We know it’s a hassle. Upgrade and come back.'
                )}
              </p>
            ) : null}
            <LegacyPlanBanner />
            <ViewTeamsAlertMessage />
            <ViewingPricingPlanWithoutPermission />
          </div>
          <div className="flex flex-col gap-12">
            <div className="flex justify-center">
              <TabGroup
                onSlTabShow={(event) => {
                  setBillingPeriod((event as any).detail.name as BillingPeriod);
                }}
              >
                <Tab
                  slot="nav"
                  panel={BillingPeriod.Yearly}
                  active={billingPeriod === BillingPeriod.Yearly}
                >
                  <div className="min-w-56 flex justify-center gap-2">
                    {translate('Annually')}
                    <Badge tone="promotion">
                      {translate('Save {0}').format('33%')}
                    </Badge>
                  </div>
                </Tab>
                <Tab
                  slot="nav"
                  panel={BillingPeriod.Monthly}
                  active={billingPeriod === BillingPeriod.Monthly}
                >
                  <div className="min-w-56 flex justify-center">
                    {translate('Monthly')}
                  </div>
                </Tab>
              </TabGroup>
            </div>
            <div className="pricing-grid">
              <PlanColumn
                billingPeriod={billingPeriod}
                planType={PlanType.Free}
              />
              <PlanColumn
                billingPeriod={billingPeriod}
                planType={PlanType.Essentials}
              />
              <PlanColumn
                isBestForTeams
                billingPeriod={billingPeriod}
                planType={PlanType.Pro}
              />
              <PlanColumn
                billingPeriod={billingPeriod}
                planType={PlanType.Organization}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function renderPricingTablePage() {
  ReactDom.render(
    <QueryClientProvider client={queryClient}>
      <PricingTablePage />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>,
    document.getElementById('main-content')
  );
}
